import React, { useMemo, useState } from "react";
import { Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { FranklinLogo } from "@/components/ui/franklin-logo";
import { useLanguage } from "@/lib/i18n/LanguageContext";
import { ProposalFolderTree } from "./ProposalFolderTree";
import {
  DIMENSION_LABEL_KEYS,
  folderNameError,
  keptDocumentCount,
  MAX_FOLDER_NAME_LENGTH,
  proposalTree,
  reachableFolders,
  reviewableMoves,
} from "../../types/organize";
import type {
  FolderOrganizeRequest,
  OrganizeDimension,
  OrganizeProposal,
  ProposalDocument,
  ProposalTreeNode,
} from "../../types/organize";

interface OrganizeProposalReviewProps {
  request: FolderOrganizeRequest;
  /** id -> row for every document in the proposal. */
  documents: Map<number, ProposalDocument>;
  /**
   * The rows are still arriving. The folders are already known (they come from
   * the proposal itself), but drawing them over file rows that read "#4821"
   * until the fetch lands looks like a broken proposal rather than a pending
   * one — so the tree waits for both.
   */
  isLoadingDocuments?: boolean;
  excludedIds: Set<number>;
  onToggleDocument: (documentId: number, excluded: boolean) => void;
  /** documentId -> the proposed folder key the reviewer dragged it onto. */
  assignmentOverrides: Map<number, string>;
  onMoveDocument: (documentId: number, targetFolderKey: string) => void;
  onPreviewDocument: (document: ProposalDocument) => void;
  onApply: (folderNameOverrides: Record<string, string>) => void;
  onDiscard: () => void;
  isApplying: boolean;
}

function interpolate(template: string, values: Record<string, string | number>): string {
  return Object.entries(values).reduce(
    (acc, [key, value]) => acc.replace(`{${key}}`, String(value)),
    template
  );
}

/**
 * Sibling groups keyed by the real parent a folder will sit under, so a rename
 * can be checked against exactly the names apply would compare it with.
 * Mirrors _sibling_bucket in organizer/tools/save_proposal.py.
 */
function siblingGroups(roots: ProposalTreeNode[]): Map<string, ProposalTreeNode[]> {
  const groups = new Map<string, ProposalTreeNode[]>();
  const walk = (nodes: ProposalTreeNode[], parentKey: string) => {
    groups.set(parentKey, nodes);
    for (const node of nodes) walk(node.children, node.folder.key);
  };
  walk(roots, "__root__");
  return groups;
}

export function OrganizeProposalReview({
  request,
  documents,
  isLoadingDocuments = false,
  excludedIds,
  onToggleDocument,
  assignmentOverrides,
  onMoveDocument,
  onPreviewDocument,
  onApply,
  onDiscard,
  isApplying,
}: OrganizeProposalReviewProps) {
  const { t } = useLanguage();
  const [renames, setRenames] = useState<Map<string, string>>(new Map());

  /**
   * The proposal as the reviewer has edited it. A drag has to be visible the
   * moment it lands — a file that stayed put until Apply would read as the drag
   * having failed — so the tree, the counts and the sibling-name checks all run
   * against this rather than the stored proposal. The stored one is untouched;
   * the overrides travel to the server separately and are validated there.
   */
  const effectiveProposal = useMemo<OrganizeProposal | null>(() => {
    if (!request.proposal || assignmentOverrides.size === 0) return request.proposal;
    return {
      ...request.proposal,
      assignments: request.proposal.assignments.map((a) => {
        const target = assignmentOverrides.get(a.document_id);
        // Forced to "move": dragging a file the agent wanted to leave alone is
        // exactly how a reviewer says "no, it belongs in there".
        return target ? { ...a, action: "move" as const, target_key: target } : a;
      }),
    };
  }, [request.proposal, assignmentOverrides]);

  const tree = useMemo(() => proposalTree(effectiveProposal), [effectiveProposal]);
  const folders = useMemo(
    () => (effectiveProposal ? reachableFolders(effectiveProposal) : []),
    [effectiveProposal]
  );

  // Renames are held here until Apply: nothing is written, so a rename costs no
  // round trip and cannot race the proposal's expiry. The server re-validates.
  const renameErrors = useMemo(() => {
    const effectiveName = (key: string, name: string) => renames.get(key) ?? name;
    const errors = new Map<string, string>();
    if (renames.size === 0) return errors;
    const groups = siblingGroups(tree);
    for (const [key, value] of renames) {
      const shape = folderNameError(value);
      if (shape === "empty") errors.set(key, t.documents.organize.renameInvalidEmpty);
      else if (shape === "slash") errors.set(key, t.documents.organize.renameInvalidSlash);
      else if (shape === "tooLong")
        errors.set(
          key,
          t.documents.organize.renameInvalidTooLong.replace("{max}", String(MAX_FOLDER_NAME_LENGTH))
        );
    }
    // A collision would be silently merged by get_or_create_folder at apply
    // time, so catch it here where the reviewer can still see both folders.
    for (const siblings of groups.values()) {
      const seen = new Map<string, string>();
      for (const node of siblings) {
        const name = effectiveName(node.folder.key, node.folder.name).trim().toLowerCase();
        if (!name) continue;
        const clash = seen.get(name);
        if (clash !== undefined) {
          for (const key of [clash, node.folder.key]) {
            if (renames.has(key) && !errors.has(key)) {
              errors.set(key, t.documents.organize.renameInvalidDuplicate);
            }
          }
        } else {
          seen.set(name, node.folder.key);
        }
      }
    }
    return errors;
  }, [renames, tree, t]);

  const keptCount = useMemo(() => keptDocumentCount(effectiveProposal), [effectiveProposal]);
  const allMoves = useMemo(() => reviewableMoves(effectiveProposal), [effectiveProposal]);
  const totalMoves = allMoves.length;
  const selectedMoves = useMemo(
    () => allMoves.filter((a) => !excludedIds.has(a.document_id)).length,
    [allMoves, excludedIds]
  );
  const newFolderCount = folders.filter(({ folder }) => folder.existing_folder_id === null).length;

  // A tags run has no prose summary — it explains itself from the dimensions.
  const tagSummary =
    request.mode === "tags" && request.dimensions?.length
      ? t.documents.organize.tagSummary.replace(
          "{dimensions}",
          request.dimensions
            .map(
              (d: OrganizeDimension) =>
                t.documents.tagFilter[
                  DIMENSION_LABEL_KEYS[d] as keyof typeof t.documents.tagFilter
                ] as string
            )
            .join(" › ")
        )
      : null;

  const handleApply = () => {
    const overrides: Record<string, string> = {};
    for (const [key, value] of renames) {
      const trimmed = value.trim();
      const original = folders.find(({ folder }) => folder.key === key)?.folder;
      if (original && trimmed && trimmed !== original.name) overrides[key] = trimmed;
    }
    onApply(overrides);
  };

  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-3">
      <div className="space-y-1.5">
        <p className="text-sm text-muted-foreground">
          {interpolate(t.documents.organize.reviewStats, {
            moves: totalMoves,
            folders: folders.length,
            newFolders: newFolderCount,
          })}
        </p>
        {tagSummary && <p className="text-xs text-muted-foreground">{tagSummary}</p>}
      </div>

      <div className="min-h-0 min-w-0 flex-1 space-y-1 overflow-auto rounded-lg border p-2">
        {isLoadingDocuments ? (
          // role="status" so the wait is announced, not just drawn. The text
          // below is the accessible name, which is why the mark stays alt="".
          <div
            role="status"
            className="flex h-full flex-col items-center justify-center gap-3 py-12"
          >
            <FranklinLogo
              size="lg"
              showBorder={false}
              alt=""
              imageClassName="animate-spin motion-reduce:animate-none"
            />
            <p className="text-center text-sm text-muted-foreground">
              {t.documents.organize.loadingProposal}
            </p>
          </div>
        ) : (
          <>
            <ProposalFolderTree
              proposal={effectiveProposal}
              documents={documents}
              excludedIds={excludedIds}
              onToggleDocument={onToggleDocument}
              onMoveDocument={onMoveDocument}
              onPreviewDocument={onPreviewDocument}
              renames={renames}
              onRename={(key, name) =>
                setRenames((prev) => {
                  const next = new Map(prev);
                  next.set(key, name);
                  return next;
                })
              }
              renameErrors={renameErrors}
            />
            {keptCount > 0 && (
              <p className="flex items-center gap-2 px-2 py-2 text-xs text-muted-foreground">
                <Undo2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {t.documents.organize.keptFiles.replace("{count}", String(keptCount))}
              </p>
            )}
          </>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t pt-3">
        <div className="min-w-0 text-xs text-muted-foreground">
          <span className="tabular-nums">
            {t.documents.organize.moveCount
              .replace("{selected}", String(selectedMoves))
              .replace("{total}", String(totalMoves))}
          </span>
          {request.expires_at && (
            <span className="ml-2 hidden sm:inline">
              {t.documents.organize.expiryNote.replace(
                "{time}",
                new Date(request.expires_at).toLocaleString(undefined, {
                  dateStyle: "short",
                  timeStyle: "short",
                })
              )}
            </span>
          )}
        </div>
        <div className="flex shrink-0 gap-2">
          <Button variant="outline" size="sm" onClick={onDiscard} disabled={isApplying}>
            {t.documents.organize.discard}
          </Button>
          <Button
            size="sm"
            onClick={handleApply}
            // A name the server would refuse blocks Apply here, so the reviewer
            // fixes it while looking at the tree rather than after a failure.
            // The loader hides the tree, so Apply waits with it: approving a
            // proposal you cannot see is exactly what this screen exists to
            // prevent.
            disabled={
              isApplying || isLoadingDocuments || selectedMoves === 0 || renameErrors.size > 0
            }
          >
            {isApplying ? t.documents.organize.applying : t.documents.organize.apply}
          </Button>
        </div>
      </div>
    </div>
  );
}
